<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import AppToast from '#/components/layout/AppToast.vue';
import { useCanonicalHead } from '#/composables/useCanonicalHead';
import { useRouteHead } from '#/composables/useRouteHead';
import BlankLayout from '#/layouts/BlankLayout.vue';
import WorkspaceLayout from '#/layouts/WorkspaceLayout.vue';

/**
 * 레이아웃 리졸버.
 * route.meta.layout('blank' | 'workspace'[기본])에 따라 셸 레이아웃을 선택하고
 * <RouterView/>(페이지)를 레이아웃 slot 에 주입한다.
 * - blank     : 셸 없음(인증·예외).
 * - workspace : AI 분석 워크스페이스 셸(WorkspaceShell GNB) — 이 앱의 유일한 제품 셸.
 *
 * AppToast 는 레이아웃 밖에 1개만 마운트 — 라우트 이동에도 살아남는 전역 알림 단일 표시기
 * (http.ts 인터셉터가 띄우는 에러 토스트의 유일한 렌더 지점).
 *
 * SEO: 공개 라우트의 canonical `<link>` 는 useCanonicalHead 가, 문서 제목·설명은 useRouteHead 가
 * 라우트 meta 기반으로 여기서 한 번만 주입한다. 페이지별 중복 선언 금지.
 */
const route = useRoute();

useRouteHead();
useCanonicalHead();

const LAYOUTS = { blank: BlankLayout, workspace: WorkspaceLayout } as const;

const layoutComponent = computed(
  () => LAYOUTS[route.meta.layout as keyof typeof LAYOUTS] ?? WorkspaceLayout,
);
</script>

<template>
  <component :is="layoutComponent">
    <RouterView v-slot="{ Component }">
      <!-- w-full: BlankLayout 의 flex 자식이 shrink-to-fit 으로 좁아져 내부 카드 max-w-[440px]
           가 무력화되는 것 방지. 중앙정렬은 레이아웃(BlankLayout)이 담당 — wrapper 에 items-center
           를 주면 DefaultLayout 섹션 배경이 content 폭으로 수축(full-bleed 깨짐)되므로 주지 않는다. -->
      <div :key="$route.path" class="w-full animate-fo-fade">
        <component :is="Component" />
      </div>
    </RouterView>
  </component>

  <AppToast />
</template>
